import { ContentState } from "draft-js";
import { I18n } from "@assets/strings";
import { useObservable } from "micro-observables";
import React, { useMemo, useRef, useState } from "react";
import { useHistory } from "@core/routing/useHistory";
import styled from "styled-components";
import { Path } from "@core/routing/path";
import { useServices } from "@core/servicesContext";
import { LightCourse } from "@domain/lms/course/course";
import { CourseDraft } from "@domain/lms/course/courseDraft";
import { useEffectAsync, useIsMobile } from "@utils/hooks";
import { PrimaryButton } from "@ui/components/buttons/primaryButton";
import { useModalTools } from "@ui/modals/modalContext";
import { desktop, gap, mobile } from "@ui/styles/utils";
import { createToast } from "@ui/toasts/utils";
import { CourseDetailsEditor } from "./courseDetailsEditor";
import { CourseEditorController } from "@ui/pages/lms/courseEditorPage/courseEditorController/courseEditorController";
import { EditorControllerContext } from "@ui/pages/lms/courseEditorPage/courseEditorController/editorControllerContext";
import { CourseMainEditor } from "./courseMainEditor";
import { PublishCourseModal } from "./publishCourseModal";

interface CourseEditorProps extends React.HTMLAttributes<HTMLDivElement> {
	initialCourse: CourseDraft;
	step: "MAIN" | "DETAILS";
	onStepChange: (step: "MAIN" | "DETAILS") => void;
	courseId?: string;
}
export const CourseEditor: React.FC<CourseEditorProps> = ({
	initialCourse,
	courseId,
	step,
	onStepChange,
	...htmlProps
}) => {
	const controller = useMemo(() => new CourseEditorController(initialCourse), []);
	const course = useObservable(controller.course);
	const history = useHistory();
	const [loading, setLoading] = useState(false);
	const { createModal } = useModalTools();
	const previousStatus = useRef(course.status);
	const { courseService, trackingService } = useServices();
	const isMobile = useIsMobile();

	const saveCourse = async (
		track?: "track",
		toast?: "toast",
		redirect?: "course" | "editor"
	): Promise<LightCourse | undefined> => {
		setLoading(true);
		const currentCourse = controller.course.get();
		try {
			if (track) {
				trackingService.track("course / save", { status: currentCourse.status });
			}
			const fetchedCourse = await courseService.saveCourse(currentCourse, courseId, currentCourse.publisher?.id);
			if (toast) {
				createToast(I18n.t("course.toast.save.title"), I18n.t("course.toast.save.message"), "success");
			}
			if (redirect === "course") {
				history.push(Path.Lms.ReferenceCourse(fetchedCourse));
			}
			if (redirect === "editor") {
				history.replace(Path.Lms.Editor(fetchedCourse.id));
			}
			return fetchedCourse;
		} catch (e) {
			console.error(e);
			createToast(I18n.t("course.toast.save.title"), I18n.t("course.toast.save.error"), "error");
			return;
		} finally {
			setLoading(false);
		}
	};

	useEffectAsync(async () => {
		if (previousStatus.current !== course.status) {
			const { confirmed, shouldShare, shareMessage } =
				course.status === "PUBLISHED"
					? await new Promise<{ confirmed: boolean; shouldShare: boolean; shareMessage?: ContentState }>((resolve) =>
							createModal((close) => (
								<PublishCourseModal
									courseTitle={course.title}
									onConfirm={(confirmed, shouldShare, shareMessage) => {
										close();
										resolve({
											confirmed,
											shouldShare,
											shareMessage,
										});
									}}
								/>
							))
						)
					: {
							confirmed: true,
							shouldShare: false,
							shareMessage: undefined,
						};
			if (!confirmed) {
				controller.setStatus(previousStatus.current);
				return;
			}

			trackingService.track(`course / ${course.status === "PUBLISHED" ? "publish" : "archive"}`);
			const savedCourse = await saveCourse();
			if (savedCourse) {
				createToast(
					I18n.t("toast.success"),
					I18n.t(`course.toast.${course.status === "PUBLISHED" ? "publish" : "archive"}.message`),
					"success"
				);
				if (shouldShare) {
					await courseService.shareCourse(savedCourse.id, shareMessage);
					trackingService.track("course / share from publish");
				}
				previousStatus.current = course.status;
				history.push(Path.Lms.ReferenceCourse(savedCourse));
			} else {
				controller.setStatus(previousStatus.current);
			}
		}
	}, [course.status]);

	return (
		<EditorControllerContext.Provider value={controller}>
			<Container {...htmlProps}>
				{isMobile ? (
					step === "MAIN" ? (
						<>
							<CourseMainEditor />
							<PrimaryButton onClick={() => onStepChange("DETAILS")} disabled={!controller.isValidForNextStep()}>
								{I18n.t("next")}
							</PrimaryButton>
						</>
					) : (
						<CourseDetailsEditor onSaveCourse={saveCourse} loading={loading} courseId={courseId} />
					)
				) : (
					<>
						<StyledMainEditor />
						<StyledCourseDetailsEditor onSaveCourse={saveCourse} loading={loading} courseId={courseId} />
					</>
				)}
			</Container>
		</EditorControllerContext.Provider>
	);
};

const Container = styled.div`
	width: 100%;
	@media ${desktop} {
		display: grid;
		grid-gap: 20px;
		grid-template-columns: auto 348px;
		grid-template-rows: auto;
		grid-template-areas:
			"main details"
			"main details"
			"main details";
	}
	@media ${mobile} {
		${gap(40)};
		> button {
			margin: 0 auto;
		}
	}
`;

const StyledMainEditor = styled(CourseMainEditor)`
	grid-area: main;
`;

const StyledCourseDetailsEditor = styled(CourseDetailsEditor)`
	grid-area: details;
`;
